import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CloseIcon, DashboardIcon, EditIcon, EyeIcon, ListIcon, SearchIcon } from './Icons';
import { useWorkspaceTabs } from '../context/WorkspaceTabsContext';
import './Layout.css';

const getTabIcon = (kind) => {
  if (kind === 'dashboard') return DashboardIcon;
  if (kind === 'search') return SearchIcon;
  if (kind === 'edit' || kind === 'create' || kind === 'settings') return EditIcon;
  if (kind === 'detail' || kind === 'view') return EyeIcon;
  return ListIcon;
};

const WorkspaceTabBar = ({ isSidebarOpen }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [draggingTabId, setDraggingTabId] = useState('');
  const [dragOverTabId, setDragOverTabId] = useState('');
  const {
    tabs,
    activeTabId,
    activateWorkspaceTab,
    closeWorkspaceTab,
    reorderWorkspaceTabs
  } = useWorkspaceTabs();

  if (tabs.length === 0) return null;

  const getFallbackPath = (tabId) => {
    const closingIndex = tabs.findIndex(tab => tab.id === tabId);
    const nextTab = tabs[closingIndex + 1] || tabs[closingIndex - 1];
    return nextTab?.path || '/';
  };

  const getCurrentTab = () => {
    const currentPath = `${location.pathname}${location.search}${location.hash}`;
    return tabs.find(tab => tab.path === currentPath) || tabs.find(tab => tab.id === activeTabId);
  };

  const handleActivate = (tab) => {
    const currentTab = getCurrentTab();
    if (
      currentTab &&
      currentTab.id !== tab.id &&
      currentTab.isDirty &&
      !window.confirm('You have unsaved changes. Switch tabs and discard them?')
    ) {
      return;
    }

    activateWorkspaceTab(tab.id);
    navigate(tab.path);
  };

  const handleTabKeyDown = (event, tab) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleActivate(tab);
    }
  };

  const handleClose = (event, tab) => {
    event.stopPropagation();
    if (tab.isDirty && !window.confirm('You have unsaved changes. Close this tab and discard them?')) {
      return;
    }

    const shouldNavigate = activeTabId === tab.id || `${location.pathname}${location.search}${location.hash}` === tab.path;
    const fallbackPath = getFallbackPath(tab.id);
    closeWorkspaceTab(tab.id);

    if (shouldNavigate) {
      navigate(fallbackPath);
    }
  };

  const handleDragStart = (event, tab) => {
    if (event.target.closest('.workspace-tab-close')) {
      event.preventDefault();
      return;
    }

    setDraggingTabId(tab.id);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', tab.id);
  };

  const handleDragOver = (event, tab) => {
    event.preventDefault();
    const sourceTabId = event.dataTransfer.getData('text/plain') || draggingTabId;
    if (!sourceTabId || sourceTabId === tab.id) return;

    event.dataTransfer.dropEffect = 'move';
    setDragOverTabId(tab.id);
  };

  const handleDragLeave = (event, tab) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setDragOverTabId(prevTabId => (prevTabId === tab.id ? '' : prevTabId));
    }
  };

  const handleDrop = (event, tab) => {
    event.preventDefault();
    const sourceTabId = event.dataTransfer.getData('text/plain') || draggingTabId;
    reorderWorkspaceTabs(sourceTabId, tab.id);
    setDraggingTabId('');
    setDragOverTabId('');
  };

  const handleDragEnd = () => {
    setDraggingTabId('');
    setDragOverTabId('');
  };

  return (
    <div className={`workspace-tab-bar ${!isSidebarOpen ? 'full-width' : ''}`}>
      <div className="workspace-tab-list" role="tablist" aria-label="Open page tabs">
        {tabs.map(tab => {
          const isActive = tab.id === activeTabId || `${location.pathname}${location.search}${location.hash}` === tab.path;
          const Icon = getTabIcon(tab.kind);
          const isDragging = tab.id === draggingTabId;
          const isDragTarget = tab.id === dragOverTabId && tab.id !== draggingTabId;

          return (
            <div
              key={tab.id}
              className={`workspace-tab ${isActive ? 'active' : ''} ${tab.isDirty ? 'dirty' : ''} ${isDragging ? 'dragging' : ''} ${isDragTarget ? 'drag-over' : ''}`}
              onClick={() => handleActivate(tab)}
              onKeyDown={(event) => handleTabKeyDown(event, tab)}
              draggable
              onDragStart={(event) => handleDragStart(event, tab)}
              onDragOver={(event) => handleDragOver(event, tab)}
              onDragLeave={(event) => handleDragLeave(event, tab)}
              onDrop={(event) => handleDrop(event, tab)}
              onDragEnd={handleDragEnd}
              role="tab"
              tabIndex={0}
              aria-selected={isActive}
              title={tab.subtitle ? `${tab.title}: ${tab.subtitle}` : tab.title}
            >
              <span className="workspace-tab-icon">
                <Icon size={14} />
              </span>
              <span className="workspace-tab-text">
                <span className="workspace-tab-title">{tab.title}</span>
                {tab.subtitle && <span className="workspace-tab-subtitle">{tab.subtitle}</span>}
              </span>
              {tab.isDirty && <span className="workspace-tab-dirty" aria-label="Unsaved changes" />}
              <button
                type="button"
                className="workspace-tab-close"
                onClick={(event) => handleClose(event, tab)}
                title="Close tab"
                aria-label={`Close ${tab.title}`}
              >
                <CloseIcon />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WorkspaceTabBar;
